import {gql} from '@apollo/client';
import {asyncNoop} from '@benchling/js-utils';
import {createStyles, ScrollArea, Stack, Text, TextInput} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  ProcedureSearchSidepanelListingQuery,
  ProcedureSearchSidepanelListingQuery_proceduresListing_ProcedureNode as ProcedureSearchSidepanelProcedureNode,
} from './__generated/ProcedureSearchSidepanelListingQuery';
import {PresentationalProcedureCard} from './ProcedureCard';
import {shouldShowProcedureForProcedureRunCreation} from './ProcedureDropdown';

export const PROCEDURE_SEARCH_SIDEPANEL_LISTING_QUERY = gql`
  query ProcedureSearchSidepanelListingQuery($procedureTypes: [ProcedureType!])
  @skipAudit
  @engComponent(name: STUDIES) {
    proceduresListing(procedureTypes: $procedureTypes, excludeArchived: true) {
      id
      name
      editUrl
      publishedProcedureFlowchartConfigVersionProxy {
        id
      }
      templateCollection {
        id
        name
      }
      currentPublishingRecord {
        ... on PublishingRecordNode {
          id
          publishingStatus
        }
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface SelectedProcedure {
  procedureId: string;
  workflowFlowchartConfigVersionId: string;
}

export const PROCEDURE_SELECTION_DND_MIME_TYPE = 'application/x-benchling-selected-procedure';

interface ProcedureSearchSidepanelProps {
  procedureType: ProcedureType;
  studyType: StudyType;
}

interface UseFilteredProceduresProps {
  procedures: Array<ProcedureSearchSidepanelProcedureNode>;
  searchQuery: string;
}

type ProcedureNodeWithPublishedPFCVP = ProcedureSearchSidepanelProcedureNode & {
  publishedProcedureFlowchartConfigVersionProxy: NonNullable<
    ProcedureSearchSidepanelProcedureNode['publishedProcedureFlowchartConfigVersionProxy']
  >;
};

function isEligibleProcedureForProcedureRunCreation(
  procedure: ProcedureSearchSidepanelProcedureNode
): procedure is ProcedureNodeWithPublishedPFCVP {
  return (
    shouldShowProcedureForProcedureRunCreation(procedure) &&
    procedure.publishedProcedureFlowchartConfigVersionProxy != null
  );
}

function useFilteredProcedures({
  procedures,
  searchQuery,
}: UseFilteredProceduresProps): Array<ProcedureNodeWithPublishedPFCVP> {
  // Filter out unpublished procedures (when publishing is enabled) and legacy (non-PoW) procedures
  const eligibleProcedures = useMemo(
    () =>
      procedures
        .filter((procedure): procedure is ProcedureNodeWithPublishedPFCVP =>
          isEligibleProcedureForProcedureRunCreation(procedure)
        )
        .toSorted(
          (a, b) =>
            a.templateCollection.name.localeCompare(b.templateCollection.name) || a.name.localeCompare(b.name)
        ),
    [procedures]
  );

  // Filter based on search query
  return useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
    if (normalizedQuery.length === 0) {
      return eligibleProcedures;
    }

    return eligibleProcedures.filter((procedure) =>
      procedure.name.toLocaleLowerCase().includes(normalizedQuery)
    );
  }, [eligibleProcedures, searchQuery]);
}

function handleProcedureCardDragStart(
  event: React.DragEvent<HTMLDivElement>,
  procedure: ProcedureNodeWithPublishedPFCVP
): void {
  const selectedProcedure: SelectedProcedure = {
    procedureId: procedure.id,
    workflowFlowchartConfigVersionId: procedure.publishedProcedureFlowchartConfigVersionProxy.id,
  };
  const payload = JSON.stringify(selectedProcedure);
  event.dataTransfer.effectAllowed = 'copy';
  event.dataTransfer.setData(PROCEDURE_SELECTION_DND_MIME_TYPE, payload);
}

export default function ProcedureSearchSidepanel({
  procedureType,
  studyType,
}: ProcedureSearchSidepanelProps): JSX.Element | null {
  const {classes} = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const procedureLibraryLabel = `${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.SENTENCE,
    plural: studyType === StudyType.ASSAY_DEVELOPMENT,
  })} library`;
  const searchInputAriaLabel = `Search ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
    plural: true,
  })}`;
  const proceduresResult = useBenchlingQuery<ProcedureSearchSidepanelListingQuery>(
    PROCEDURE_SEARCH_SIDEPANEL_LISTING_QUERY,
    {
      variables: {procedureTypes: [procedureType]},
      renderCachedDataWhenRefetching: true,
    }
  );
  const procedures =
    proceduresResult.type === QueryResultType.SUCCESS ? proceduresResult.data.proceduresListing : [];
  const filteredProcedures = useFilteredProcedures({procedures, searchQuery});

  if (proceduresResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  if (proceduresResult.type === QueryResultType.ERROR) {
    return null;
  }

  return (
    <Stack spacing={0} className={classes.container}>
      <TextInput
        className={classes.queryInput}
        placeholder="Search"
        aria-label={searchInputAriaLabel}
        value={searchQuery}
        onChange={(event) => {
          setSearchQuery(event.currentTarget.value);
        }}
        {...getTestProps({component: 'ProcedureSearchSidepanel', element: 'queryInput'})}
      />
      <Text className={classes.libraryLabel}>{procedureLibraryLabel}</Text>
      <ScrollArea className={classes.resultsScrollArea} offsetScrollbars={true}>
        <Stack spacing={16}>
          {filteredProcedures.map((procedure) => (
            <PresentationalProcedureCard
              key={procedure.id}
              procedureId={procedure.id}
              flowchartVersionId={procedure.publishedProcedureFlowchartConfigVersionProxy.id}
              procedureName={procedure.name}
              templateCollectionName={procedure.templateCollection.name}
              procedureType={procedureType}
              studyType={studyType}
              onProcedureChange={asyncNoop} // Does not allow changing procedure
              isEditable={false} // Does not allow removing procedure
              hasUpdateAvailable={false} // Does not allow updating procedure
              onOpenUpdateModal={() => {}}
              isDraggable={true}
              onDragStart={(event) => {
                handleProcedureCardDragStart(event, procedure);
              }}
            />
          ))}
          {filteredProcedures.length === 0 && (
            <Text className={classes.emptyResultsText}>{`No ${procedureString}s found.`}</Text>
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    height: '100%',
    padding: `${theme.spacing.lg} ${theme.spacing.md}`,
  },
  resultsScrollArea: {
    flex: 1,
    minHeight: 0,
  },
  queryInput: {
    marginBottom: theme.spacing.lg,
  },
  libraryLabel: {
    color: theme.black,
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    lineHeight: '14px',
    marginBottom: theme.spacing.md,
  },
  emptyResultsText: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.sm,
  },
}));
